import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { GripVertical, Plus, Trash2, Check, Lock, RotateCcw } from 'lucide-react';

import '../design/tokens.css';
import { db } from '../firebase';
import { Badge, Btn, EmptyState, Segmented } from '../design/ui';
import { Lbl, Inp, Ta, Kad, Nota } from './prostudioUI';
import { blokDariMasterPrompt, SEMPADAN_GOYANG } from '../utils/goyangBlok';
import { bacaProBlok, simpanProBlok, cetakBlok } from '../utils/proBlokStore';

// ============================================================
// ★ BLOCKS — seksyen dalam PRO Studio, module GOYANG (Fasa 1)
//
// Rujukan penuh: PLAN_PROSTUDIO_BLOCKS.md (root).
//
// Apa skrin ini buat:
//   • Baca blocks yang PELANGGAN sudah simpan (users/{uid}/proBlocks/
//     goyang). Belum ada → pecahkan master prompt GOYANG hidup dari peti
//     besi menjadi 13 block (teks asal, tiada apa berubah).
//   • Save → tulis SATU doc (semua block, ikut susunan). Lihat
//     proBlokStore.js untuk sebab satu-doc + semakan nilai.
//   • Kembali ke asal → muat semula pecahan peti besi ke dalam editor.
//     TIDAK menulis apa-apa sehingga Save ditekan.
//   • Dipapar kepada ADMIN sahaja (Fasa 1) — pagar `isAdmin` dalam
//     ProStudioV39View menyorok barisnya DAN melencongkan alamatnya.
//
// Konsep (dikunci dengan user 2026-09-08/09):
//   • Pro Mode HIDUP = master prompt dibina daripada blocks ini.
//     Pro Mode MATI  = enjin default, tidak disentuh langsung.
//   • Susunan block = susunan dalam prompt akhir. Sebab itu senarai
//     ini menegak + boleh diseret, bukan grid kad.
//   • Output format (nama medan JSON + placeholder [HOOK]) BUKAN
//     block. Ia plumbing: kalau ia berubah, pelanggan bukan dapat
//     hasil lain — dia dapat card KOSONG tanpa error.
//   • "Use exactly" = AKAN DATANG. Ia perlu keputusan "teks masuk medan
//     mana" yang user tangguh; pilihan itu DIKUNCI supaya ia bukan
//     kawalan palsu (feedback_no_fake_controls), bukan disorok.
//
// ⭐ TEKS ASAL DIBACA HIDUP DARI PETI BESI, BUKAN DIKERASKAN DALAM
//    FAIL INI. Peraturan pangkalan data mengunci modules/{id}/private/
//    prompt kepada admin sahaja, jadi master prompt TIDAK masuk ke
//    dalam bundle webapp dan tiada pelanggan boleh membacanya melalui
//    DevTools.
//
// ⛔ TIADA SANDARAN SENYAP. Bacaan gagal → skrin memberitahu terus.
//    Save gagal → mesej sebab dipapar, bukan kilat "Disimpan" palsu.
// ============================================================

const MODULE_ID = 'goyang';

/* Dua keadaan sahaja, jadi ia suis — bukan menu. Diletak di penjuru
   kanan atas editor (arahan user 2026-09-09) supaya ia menonjol dan
   tidak terselit antara kotak teks. `exact` dikunci Fasa 1. */
const MODE = [
    { value: 'ai', label: 'AI generate' },
    { value: 'exact', label: 'Use exactly', disabled: true },
];

const idBaharu = () => `b_${Math.random().toString(36).slice(2, 8)}`;

/* Pecahan peti besi. Di luar komponen kerana ia tidak bergantung pada
   apa-apa keadaan — dipakai oleh muat pertama (tiada doc tersimpan)
   DAN oleh "Kembali ke asal". */
async function muatBlokAsal() {
    const snap = await getDoc(doc(db, 'modules', MODULE_ID, 'private', 'prompt'));
    if (!snap.exists()) throw new Error('Peti besi master prompt GOYANG tidak dijumpai.');
    return blokDariMasterPrompt(snap.data()?.masterPrompt).blocks;
}

const NAMA_PLUMBING = SEMPADAN_GOYANG.filter((s) => s.jenis === 'plumbing').map((s) => s.nama);

const teksRalat = (e, lalai) => (e?.code === 'permission-denied'
    ? 'Tiada kebenaran. Peraturan pangkalan data untuk Blocks mungkin belum naik, atau akaun ini bukan admin.'
    : (e?.message || lalai));

/* ============================================================
   Baris dalam senarai. Diasingkan supaya logik seret tidak
   bercampur dengan borang di sebelah kanan.
   ============================================================ */
const BarisBlok = ({ blok, aktif, onPilih, onPadam, seret }) => (
    <div
        draggable
        onDragStart={seret.mula}
        onDragOver={seret.atas}
        onDrop={seret.jatuh}
        onDragEnd={seret.habis}
        onClick={onPilih}
        /* ⚠️ `minWidth: 0` WAJIB — baris ini ialah grid item, dan grid item
           lalainya `min-width: auto`, iaitu ia enggan mengecil di bawah saiz
           kandungannya. Tanpa ia, nama block yang panjang menolak baris
           melimpah keluar kad dan bertindih dengan editor di sebelah kanan
           (diukur pada localhost: baris 274px dalam kad 266px). */
        style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px', borderRadius: 9, cursor: 'pointer',
            minWidth: 0, overflow: 'hidden',
            background: aktif ? 'var(--s-fill)' : 'transparent',
            border: `1px solid ${aktif ? 'var(--s-line-strong)' : 'transparent'}`,
            opacity: seret.diseret ? 0.45 : 1,
        }}
    >
        <span style={{ display: 'flex', flex: 'none', color: 'var(--s-ink-3)', cursor: 'grab' }} title="Seret untuk susun">
            <GripVertical size={14} strokeWidth={1.9} />
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
                fontSize: 13.5, fontWeight: 500, color: 'var(--s-ink)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{blok.nama}</div>
        </div>

        {/* `flex: none` supaya lencana dan tong sampah tidak dimampatkan
            menjadi comot apabila nama block panjang. */}
        <span style={{ flex: 'none', display: 'flex' }}>
            <Badge tone="neutral">{blok.mod === 'exact' ? 'EXACT' : 'AI'}</Badge>
        </span>

        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPadam(); }}
            title="Padam block"
            style={{
                display: 'flex', flex: 'none', padding: 2, background: 'none', border: 0,
                cursor: 'pointer', color: 'var(--s-ink-3)',
            }}
        >
            <Trash2 size={14} strokeWidth={1.9} />
        </button>
    </div>
);

/* ============================================================
   SEKSYEN
   ============================================================ */
export default function ProStudioBlokSection() {
    const [blocks, setBlocks] = useState([]);
    const [pilih, setPilih] = useState('');
    const [kilat, setKilat] = useState(false);
    const [diseret, setDiseret] = useState(null);
    const [memuat, setMemuat] = useState(true);
    const [ralat, setRalat] = useState('');

    /* Dari mana senarai semasa datang: 'akaun' = doc tersimpan pelanggan,
       'asal' = pecahan peti besi (belum pernah disimpan, atau baru direset). */
    const [sumber, setSumber] = useState('asal');
    const [disimpanPada, setDisimpanPada] = useState(null);
    /* Cetakan senarai pada kali terakhir dimuat / disimpan — pembanding
       "ada perubahan belum disimpan". */
    const [rujukan, setRujukan] = useState('');
    const [menyimpan, setMenyimpan] = useState(false);
    const [mereset, setMereset] = useState(false);
    const [ralatSimpan, setRalatSimpan] = useState('');

    const kotor = cetakBlok(blocks) !== rujukan;
    /* Belum pernah disimpan → Save sentiasa dibenarkan walau tiada
       perubahan: pelanggan perlu menyimpan default supaya Pro Mode boleh
       memakainya. Sudah tersimpan → Save hanya bila ada perubahan. */
    const bolehSimpan = !menyimpan && (kotor || sumber === 'asal');

    /* Muat pertama: doc tersimpan dahulu, peti besi hanya kalau tiada. */
    useEffect(() => {
        let dibatalkan = false;
        (async () => {
            try {
                const tersimpan = await bacaProBlok(MODULE_ID);
                if (dibatalkan) return;
                let senarai;
                if (tersimpan && tersimpan.blocks.length) {
                    senarai = tersimpan.blocks;
                    setSumber('akaun');
                    setDisimpanPada(tersimpan.updatedAt);
                } else {
                    senarai = await muatBlokAsal();
                    if (dibatalkan) return;
                    setSumber('asal');
                }
                setBlocks(senarai);
                setRujukan(cetakBlok(senarai));
                setPilih(senarai[0]?.id || '');
            } catch (e) {
                if (dibatalkan) return;
                setRalat(teksRalat(e, 'Gagal membaca blocks.'));
            } finally {
                if (!dibatalkan) setMemuat(false);
            }
        })();
        return () => { dibatalkan = true; };
    }, []);

    const semasa = blocks.find((b) => b.id === pilih) || null;

    const ubah = (patch) => setBlocks((senarai) => senarai.map(
        (b) => (b.id === pilih ? { ...b, ...patch } : b),
    ));

    const tambah = () => {
        const b = { id: idBaharu(), nama: 'Block baharu', mod: 'ai', arahan: '', contoh: '', teks: '' };
        setBlocks((senarai) => [...senarai, b]);
        setPilih(b.id);
    };

    const padam = (id) => setBlocks((senarai) => {
        const baki = senarai.filter((b) => b.id !== id);
        if (id === pilih) setPilih(baki[0]?.id || '');
        return baki;
    });

    /* Susun semula. Sengaja seret, bukan anak panah: susunan block
       ialah susunan prompt akhir, jadi ia perlu dirasa seperti
       menyusun senarai, bukan menekan butang berulang kali. */
    const jatuhkan = (indexSasaran) => {
        if (diseret === null || diseret === indexSasaran) return;
        setBlocks((senarai) => {
            const salinan = [...senarai];
            const [pindah] = salinan.splice(diseret, 1);
            salinan.splice(indexSasaran, 0, pindah);
            return salinan;
        });
        setDiseret(null);
    };

    const simpan = async () => {
        setRalatSimpan('');
        setMenyimpan(true);
        try {
            /* Nilai yang dipulangkan = nilai yang benar-benar ditulis
               (nama dikemas, medan dinormalkan) — skrin ikut doc, bukan
               sebaliknya. */
            const bersih = await simpanProBlok(MODULE_ID, blocks);
            setBlocks(bersih);
            setRujukan(cetakBlok(bersih));
            setSumber('akaun');
            setDisimpanPada(new Date());
            setKilat(true);
            setTimeout(() => setKilat(false), 1500);
        } catch (e) {
            setRalatSimpan(teksRalat(e, 'Gagal menyimpan.'));
        } finally {
            setMenyimpan(false);
        }
    };

    const kembaliAsal = async () => {
        const ok = window.confirm(
            'Gantikan semua block dengan master prompt asal?\n\n'
            + 'Perubahan yang belum disimpan akan hilang. Tiada apa ditulis ke akaun sehingga anda tekan Save.',
        );
        if (!ok) return;
        setRalatSimpan('');
        setMereset(true);
        try {
            const senarai = await muatBlokAsal();
            setBlocks(senarai);
            setPilih(senarai[0]?.id || '');
            /* `rujukan` SENGAJA tidak disentuh — senarai asal ini berbeza
               daripada yang tersimpan, jadi Save mesti kelihatan perlu. */
        } catch (e) {
            setRalatSimpan(teksRalat(e, 'Gagal membaca master prompt asal.'));
        } finally {
            setMereset(false);
        }
    };

    if (memuat) {
        return (
            <div className="s-dim" style={{ padding: '18px 2px' }}>
                Membaca blocks GOYANG…
            </div>
        );
    }

    /* Gagal = berhenti dan beritahu. JANGAN papar apa-apa teks ganti —
       user akan menilai block berdasarkan teks yang bukan teks sebenar. */
    if (ralat) {
        return <Nota jenis="warn">{ralat}</Nota>;
    }

    const statusTeks = kotor
        ? 'Ada perubahan belum disimpan.'
        : sumber === 'akaun'
            ? `Disimpan ke akaun anda${disimpanPada ? ` · ${disimpanPada.toLocaleString('ms-MY')}` : ''}.`
            : 'Teks asal daripada master prompt — belum disimpan ke akaun. Tekan Save supaya Pro Mode boleh menggunakannya.';

    return (
        <>
            <div style={{ marginBottom: 12 }}>
                <Nota jenis="info">
                    Teks asal dibaca hidup daripada server. <b>Save</b> menyimpan salinan anda ke akaun —
                    Pro Mode di Workspace akan menggunakan salinan itu.
                </Nota>
            </div>

            {/* Bar status + Kembali ke asal. Status di kiri supaya mata terus
                tahu keadaan semasa; butang reset di kanan, jauh daripada Save. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <span className="s-dim" style={{ flex: '1 1 260px', minWidth: 0 }}>{statusTeks}</span>
                <Btn
                    variant="ghost" size="sm" onClick={kembaliAsal} disabled={mereset}
                    icon={<RotateCcw size={14} strokeWidth={2} />}
                >
                    {mereset ? 'Memuat…' : 'Kembali ke asal'}
                </Btn>
            </div>

            {ralatSimpan && (
                <div style={{ marginBottom: 14 }}>
                    <Nota jenis="warn">{ralatSimpan}</Nota>
                </div>
            )}

            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* ── Senarai block ───────────────────────────────── */}
                <div style={{ flex: '1 1 240px', maxWidth: 300, minWidth: 0 }}>
                    <Kad
                        tajuk="Blocks"
                        perihal="Susunan di sini ialah susunan dalam prompt akhir."
                        kanan={<Badge tone="neutral">{blocks.length}</Badge>}
                        kaki={
                            <Btn
                                variant="ghost" size="sm" onClick={tambah}
                                icon={<Plus size={14} strokeWidth={2} />}
                                style={{ width: '100%' }}
                            >
                                New block
                            </Btn>
                        }
                    >
                        <div style={{ display: 'grid', gap: 4 }}>
                            {blocks.length === 0 && (
                                <span className="s-dim">Tiada block. Tekan New block untuk mula.</span>
                            )}
                            {blocks.map((b, i) => (
                                <BarisBlok
                                    key={b.id}
                                    blok={b}
                                    aktif={b.id === pilih}
                                    onPilih={() => setPilih(b.id)}
                                    onPadam={() => padam(b.id)}
                                    seret={{
                                        diseret: diseret === i,
                                        mula: () => setDiseret(i),
                                        atas: (e) => e.preventDefault(),
                                        jatuh: (e) => { e.preventDefault(); jatuhkan(i); },
                                        habis: () => setDiseret(null),
                                    }}
                                />
                            ))}
                        </div>

                        {/* Plumbing dinyatakan terus di sini supaya tiada
                            orang tertanya kenapa ia tiada dalam senarai. */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            marginTop: 12, paddingTop: 10,
                            borderTop: '1px solid var(--s-line)',
                            color: 'var(--s-ink-3)', fontSize: 11.5, lineHeight: 1.5,
                        }}>
                            <Lock size={12} strokeWidth={2} style={{ flex: 'none' }} />
                            <span>
                                Dikunci sistem, bukan block: {NAMA_PLUMBING.join(' · ')}.
                            </span>
                        </div>
                    </Kad>
                </div>

                {/* ── Editor ──────────────────────────────────────── */}
                <div style={{ flex: '999 1 420px', minWidth: 0, display: 'grid', gap: 14 }}>
                    {semasa ? (
                        <div className="s-panelbox" style={{ padding: 14, display: 'grid', gap: 14 }}>
                            {/* Nama di kiri, Mode di KANAN ATAS — arahan user
                                2026-09-09: mode kena diasingkan supaya ia
                                menonjol, bukan terselit antara medan teks. */}
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                                    <Lbl>Block name</Lbl>
                                    <Inp
                                        value={semasa.nama}
                                        onChange={(e) => ubah({ nama: e.target.value })}
                                    />
                                </div>
                                <div style={{ flex: '0 0 auto' }}>
                                    <Lbl>Mode</Lbl>
                                    <Segmented
                                        value={semasa.mod}
                                        onChange={(v) => ubah({ mod: v })}
                                        options={MODE}
                                    />
                                    <div className="s-dim" style={{ fontSize: 11.5, marginTop: 5 }}>
                                        Use exactly — akan datang.
                                    </div>
                                </div>
                            </div>

                            {semasa.mod === 'ai' ? (
                                <>
                                    <div>
                                        <Lbl>Instruction</Lbl>
                                        <Ta
                                            rows={6}
                                            value={semasa.arahan || ''}
                                            onChange={(e) => ubah({ arahan: e.target.value })}
                                            placeholder="Apa yang AI perlu hasilkan untuk block ini."
                                            style={{ minHeight: 132 }}
                                        />
                                    </div>
                                    <div>
                                        <Lbl>Example</Lbl>
                                        <Ta
                                            rows={4}
                                            value={semasa.contoh || ''}
                                            onChange={(e) => ubah({ contoh: e.target.value })}
                                            placeholder="Contoh rupa hasil yang anda mahu. AI tiru gayanya, bukan isinya."
                                            style={{ minHeight: 92 }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <Lbl>Prompt</Lbl>
                                        <Ta
                                            rows={8}
                                            value={semasa.teks || ''}
                                            onChange={(e) => ubah({ teks: e.target.value })}
                                            placeholder="Tampal prompt yang anda mahu digunakan seperti ini juga."
                                            style={{ minHeight: 170 }}
                                        />
                                    </div>
                                    <Nota jenis="info">
                                        Teks ini tidak dihantar kepada AI untuk ditulis semula. SESAAT
                                        mencantumnya sendiri selepas AI siap, jadi ia mustahil berubah.
                                    </Nota>
                                </>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Btn
                                    onClick={simpan}
                                    disabled={!bolehSimpan}
                                    icon={kilat ? <Check size={14} strokeWidth={2} /> : null}
                                >
                                    {menyimpan ? 'Menyimpan…' : kilat ? 'Disimpan' : 'Save'}
                                </Btn>
                            </div>
                        </div>
                    ) : (
                        <EmptyState title="Tiada block dipilih">
                            Pilih satu block di sebelah kiri, atau tekan New block.
                        </EmptyState>
                    )}
                </div>
            </div>
        </>
    );
}
